import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { styled } from '@linaria/react'
import { css } from '@linaria/core';

const BottomSheetContainer = styled.div<BottomSheetDragProps>`
  display: ${props=>props.mobileOnly ? 'none' : 'block'};
  z-index: 998;
  width: 100%;
  position:fixed;
  background-color:#fff;
  bottom:0;
  left:0;
  right:0;
  border-radius: 10px 10px 0 0;
  box-shadow: 0px 0px 0px #c4c4c4, 0px -1px 3px #c4c4c4;
  @media screen and (max-width: 768px) {
    display:block;
  }
`;

const IconContainerWrapper = styled.div`
  display: block;
  height: 36px;
  cursor: pointer;
`;

const IconContainer = styled.div`
  position: absolute;
  width: 80px;
  height: 4px;
  left: calc(50% - 80px/2 + 0.5px);
  top: 4px;
  background: #8A8988;
  border-radius: 55px;
  flex: none;
  order: 1;
  flex-grow: 0;
  z-index: 1;
`;

const ContentContainerWrapper = styled.div<BottomSheetDragProps>`
  display: block;
  padding: ${props => props.padding ? props.padding : "0 24px 36px 24px"};
`;

const BottomContentContainer = styled.div`
  z-index: 999;
  width: 100%;
  display:block;
  max-height: auto;
  transition: max-height 3.3s linear;
`;

const OverlayDiv = styled.div<BottomSheetDragProps>`
  z-index: 997;
  position:fixed;
  right:0;
  top:0;
  bottom:0;
  left:0;
  opacity: 0;
  visibility: hidden;
  background-color:rgba(1,1,1,0.5);
  transition:visibility 0.3s, opacity 0.3s linear;
 
`;

const OverlayDivVisible = css`
  opacity: 1;
  visibility: visible;
  @media screen and (min-width: 768px) {
    opacity: 0;
    visibility: hidden;
  }
`
const SheetContainerWrapper = styled.div<BottomSheetDragProps>`
  visibility:visible;
  @media screen and (min-width: 768px) {
    visibility:${props=>props.mobileOnly ? 'hidden' : 'visible'};
  }
`;

export interface BottomSheetDragProps {
  children?: React.ReactNode;
  overlayClick?: () => void;
  isShow?: boolean;
  enableDrag?: boolean;
  mobileOnly?: boolean;
  padding?: string;
}

const BottomSheetDrag = forwardRef(({ padding, isShow, mobileOnly=true, enableDrag = true, overlayClick, children }: BottomSheetDragProps, ref) => {

  const [isVisible, setIsVisible] = useState<boolean>(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const dragAreaRef = useRef<HTMLDivElement>(null);
  const topBufferSpace = 0;

  useImperativeHandle(ref, () => ({
    open() {
      setIsVisible(true);
    },
    close() {
      setIsVisible(false);
    },
  }));

  useEffect(() => {
    if (isShow) {
      setIsVisible(isShow);
    }
    else {
      setIsVisible(false);
    }
  }, [isShow]);


  useEffect(() => {
    if (isVisible) {
      document.body.style.overflow = "hidden";
      document.body.style.touchAction = "none";

      if (contentRef.current) {
        contentRef.current.style.maxHeight = "800px";
        contentRef.current.style.height = "";
        contentRef.current.style.transition = "max-height 0.3s linear";
      }
    }
    else {
      if (contentRef.current) {
        contentRef.current.style.transition = "max-height 0.3s linear";
        contentRef.current.style.maxHeight = "0px";
      }
      document.body.style.overflow = "unset";
      document.body.style.touchAction = "unset";
      disposeEventListner();
    }
  }, [isVisible]);

  useEffect(() => {
    let initHeight: number = 0;

    if (dragAreaRef && dragAreaRef.current) {
      dragAreaRef.current.addEventListener('touchstart', (e: any) => {
        if (initHeight === 0) {
          initHeight = contentRef.current?.offsetHeight ?? 0;
        }
      });
     
      dragAreaRef.current.addEventListener('touchend', (e: any) => {
        if (contentRef && contentRef.current) {
          if (contentRef.current.offsetHeight <= initHeight / 2) {
            setIsVisible(false);
          }
          else if (contentRef.current.offsetHeight >= initHeight + topBufferSpace) {
            contentRef.current.style.height = "unset";
          }
        }
      });

      dragAreaRef.current.addEventListener('touchmove', (e: any) => {
        let clientY = e.touches[0].clientY;
        if (contentRef && contentRef.current && enableDrag) {
          if (window.innerHeight - clientY <= window.innerHeight / 1.2 && window.innerHeight - clientY <= initHeight) {
            contentRef.current.style.height = (window.innerHeight - clientY) + "px";
          }
        }
      });

    }
  }, [enableDrag]);

  const disposeEventListner = () => {
    dragAreaRef.current?.removeEventListener('touchstart', (e: any) => { });
    dragAreaRef.current?.removeEventListener('touchend', (e: any) => { });
    dragAreaRef.current?.removeEventListener('touchmove', (e: any) => { });

  }
  return (
    <SheetContainerWrapper mobileOnly={mobileOnly}>
      <OverlayDiv onClick={() => {
        setIsVisible(false);
        if (overlayClick) {
          overlayClick();
        }
      }} className={isVisible ? `${OverlayDiv} ${OverlayDivVisible}` : `${OverlayDiv}`}></OverlayDiv>
      <BottomSheetContainer mobileOnly={mobileOnly} ref={contentRef}>
        <BottomContentContainer>
          <IconContainerWrapper ref={dragAreaRef}>
            <IconContainer />
          </IconContainerWrapper>
          <ContentContainerWrapper padding={padding}>
            {children}
          </ContentContainerWrapper>
        </BottomContentContainer>
      </BottomSheetContainer>
    </SheetContainerWrapper>
  )
});

export default BottomSheetDrag
